import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import {
  Category,
  Collection,
  ProductType,
  SiteSettings,
  TaxonomyInput,
} from '../models/catalog.model';
import { Product } from '../models/product.model';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface ProductQuery {
  category?: string;
  collection?: string;
  type?: string;
  filter?: 'new' | 'trending' | 'featured' | 'sale';
  q?: string;
}

/**
 * All public catalog reads live here. The navigation-critical lists
 * (categories, collections, settings) are fetched once per session and
 * shared between every subscriber, so the navbar, footer, shop menu and
 * pages never trigger duplicate HTTP requests.
 */
@Injectable({ providedIn: 'root' })
export class CatalogService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly baseUrl = environment.apiBaseUrl;

  readonly categories$: Observable<Category[]> = this.http
    .get<Category[]>(`${this.baseUrl}/categories`)
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly collections$: Observable<Collection[]> = this.http
    .get<Collection[]>(`${this.baseUrl}/collections`)
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  readonly settings$: Observable<SiteSettings> = this.http
    .get<SiteSettings>(`${this.baseUrl}/settings`)
    .pipe(shareReplay({ bufferSize: 1, refCount: false }));

  getProducts(query: ProductQuery = {}): Observable<Product[]> {
    let params = new HttpParams();
    if (query.category) params = params.set('category', query.category);
    if (query.collection) params = params.set('collection', query.collection);
    if (query.type) params = params.set('type', query.type);
    if (query.filter) params = params.set('filter', query.filter);
    if (query.q) params = params.set('q', query.q);
    return this.http.get<Product[]>(`${this.baseUrl}/products`, { params });
  }

  getProduct(idOrSlug: string | number): Observable<Product> {
    return this.http.get<Product>(`${this.baseUrl}/products/${idOrSlug}`);
  }

  // ------------------------------------------------------------------
  // Admin operations (require a valid bearer token)
  // ------------------------------------------------------------------

  adminList<T>(resource: 'categories' | 'collections' | 'product-types'): Observable<T[]> {
    return this.http.get<T[]>(`${this.baseUrl}/${resource}`, {
      params: new HttpParams().set('all', '1'),
      headers: this.auth.authHeaders(),
    });
  }

  adminCreate<T>(
    resource: 'categories' | 'collections' | 'product-types',
    input: TaxonomyInput
  ): Observable<T> {
    return this.http.post<T>(`${this.baseUrl}/${resource}`, input, {
      headers: this.auth.authHeaders(),
    });
  }

  adminUpdate<T>(
    resource: 'categories' | 'collections' | 'product-types',
    id: number,
    input: TaxonomyInput
  ): Observable<T> {
    return this.http.put<T>(`${this.baseUrl}/${resource}/${id}`, input, {
      headers: this.auth.authHeaders(),
    });
  }

  adminDelete(
    resource: 'categories' | 'collections' | 'product-types',
    id: number
  ): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${resource}/${id}`, {
      headers: this.auth.authHeaders(),
    });
  }

  adminUpdateSettings(settings: SiteSettings): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/settings`, settings, {
      headers: this.auth.authHeaders(),
    });
  }

  adminTypes(): Observable<ProductType[]> {
    return this.adminList<ProductType>('product-types');
  }
}
